import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { TodoItem } from "@zero/types/todo";
import type { KanbanColumn as KanbanColumnType } from "../../types/kanban";
import KanbanCard from "./KanbanCard";

type Props = {
  column: KanbanColumnType;
  todos: TodoItem[];
  disabled?: boolean;
  onOpenTodo?: (id: string) => void;
};

export default function KanbanColumn({ column, todos, disabled = false, onOpenTodo }: Props) {
  const { setNodeRef } = useDroppable({
    id: column.id,
  });

  const taskIds = todos.map((t) => t.id);

  const getColumnStyles = (columnId: string) => {
    switch (columnId) {
      case "todo":
        return "bg-gray-100 dark:bg-gray-800/50";
      case "in-progress":
        return "bg-blue-50 dark:bg-blue-950/30";
      case "done":
        return "bg-green-50 dark:bg-green-950/30";
      default:
        return "bg-gray-100 dark:bg-gray-800/50";
    }
  };

  const getHeaderColor = (columnId: string) => {
    switch (columnId) {
      case "todo":
        return "text-gray-700 dark:text-gray-300";
      case "in-progress":
        return "text-blue-700 dark:text-blue-300";
      case "done":
        return "text-green-700 dark:text-green-300";
      default:
        return "text-gray-700 dark:text-gray-300";
    }
  };

  return (
    <div
      className={`
                flex flex-col w-80 min-w-[320px] rounded-xl p-4
                ${getColumnStyles(column.id)}
            `}
    >
      {/* 列头 */}
      <header
        className={`
                    flex items-center justify-between mb-4 pb-2
                    border-b border-border
                `}
      >
        <div className="flex items-center gap-2">
          <h2 className={`font-semibold text-base ${getHeaderColor(column.id)}`}>{column.title}</h2>
          <span
            className={`
                            text-sm px-2 py-0.5 rounded-full
                            ${getHeaderColor(column.id)}
                            bg-black/5 dark:bg-white/10
                        `}
          >
            {todos.length}
          </span>
        </div>
      </header>

      {/* 任务列表 */}
      <div ref={setNodeRef} className="flex-1 space-y-3 min-h-[100px]">
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {todos.map((todo) => (
            <KanbanCard key={todo.id} todo={todo} disabled={disabled} onOpen={onOpenTodo} />
          ))}
        </SortableContext>
      </div>

      {/* 空状态 */}
      {todos.length === 0 && (
        <div className="flex-1 flex items-center justify-center min-h-[120px] text-muted-foreground text-sm">
          暂无任务
        </div>
      )}
    </div>
  );
}
